import { useState } from "react";
import { Marker, Popup, Circle } from "react-leaflet";
import L from "leaflet";
import { Drama, ExternalLink, X, Clock, MapPin, Car } from "lucide-react";
import { showStart } from "../lib/geo";

export const THEATRE_RADIUS = 500;

export const TheatreRadius = ({ data }) => (
  <Circle center={[data.theatre.lat, data.theatre.lng]} radius={THEATRE_RADIUS}
    pathOptions={{ color: "#F59E0B", weight: 2, dashArray: "6 6", fillColor: "#F59E0B", fillOpacity: 0.08 }} />
);

const theatreIcon = L.divIcon({ className: "theatre-pin", html: '<div class="badge">Teatro Torpignattara</div>', iconSize: [0, 0] });

const fmtDate = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });

export const TheatreMarker = ({ data }) => (
  <Marker position={[data.theatre.lat, data.theatre.lng]} icon={theatreIcon}>
    <Popup>
      <p className="font-display font-bold text-base m-0">{data.theatre.name}</p>
      <p className="text-xs text-stone-400 m-0">{data.theatre.address}</p>
      {data.next && <p className="text-sm mt-2 mb-0">Prossimo: <b>{data.next.title}</b> · {fmtDate(data.next.date)} {data.next.time}</p>}
    </Popup>
  </Marker>
);

const ShowTiming = ({ show, now }) => {
  const start = showStart(show);
  if (!start) return null;
  const mins = Math.round((start.getTime() - now) / 60000);
  const hhmm = (d) => d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  const from = new Date(start.getTime() - 3600000);
  if (mins <= 0 || mins > 24 * 60) {
    return <p data-testid="next-show-timing" className="text-xs text-stone-400 mt-2">Consiglio: cerca parcheggio dalle {hhmm(from)}, un'ora prima dell'inizio.</p>;
  }
  if (mins <= 60) {
    return <p data-testid="next-show-timing" className="text-xs font-semibold text-emerald-300 mt-2">Inizia tra {mins} min: è il momento di cercare posto!</p>;
  }
  return <p data-testid="next-show-timing" className="text-xs text-stone-300 mt-2">Oggi alle {hhmm(start)} · cerca posto dalle {hhmm(from)}</p>;
};

export const NextShowCard = ({ data, now, onFindParking }) => {
  const [open, setOpen] = useState(true);
  const s = data.next;
  if (!open) {
    return (
      <button data-testid="next-show-reopen" onClick={() => setOpen(true)} className="absolute z-[850] right-3 bottom-24 2xl:bottom-6 md:right-6 h-12 px-4 rounded-full bg-amber-500 text-stone-950 font-semibold inline-flex items-center gap-2 shadow-2xl hover:bg-amber-400 transition-colors">
        <Drama className="h-5 w-5" /> Stasera a teatro?
      </button>
    );
  }
  return (
    <div data-testid="next-show-card" className="absolute z-[850] left-3 right-3 bottom-24 2xl:bottom-6 md:left-auto md:right-6 md:w-96 rounded-2xl overflow-hidden bg-stone-900/90 backdrop-blur-xl border border-amber-500/30 shadow-2xl rise flex">
      {s.image && <img src={s.image} alt={s.title} className="w-24 sm:w-28 object-cover shrink-0" />}
      <div className="p-4 flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className="font-mono-x text-[10px] uppercase tracking-wider text-amber-400">Prossimo spettacolo</p>
          <button data-testid="next-show-close" onClick={() => setOpen(false)} className="-mt-1 -mr-1 h-7 w-7 grid place-items-center rounded-full hover:bg-stone-800 transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>
        <p data-testid="next-show-title" className="font-display text-lg font-extrabold leading-tight truncate">{s.title}</p>
        <p data-testid="next-show-date" className="text-xs text-stone-300 mt-1 flex items-center gap-1 capitalize">
          <Clock className="h-3.5 w-3.5 text-amber-400" /> {fmtDate(s.date)} · {s.time}
        </p>
        <p className="text-xs text-stone-400 mt-1 flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-amber-400" /> {data.theatre.address}</p>
        <ShowTiming show={s} now={now} />
        <button data-testid="next-show-find-parking" onClick={onFindParking} className="mt-3 w-full h-10 rounded-full bg-amber-500 hover:bg-amber-400 text-stone-950 text-sm font-bold inline-flex items-center justify-center gap-2 transition-colors">
          <Car className="h-4 w-4" /> Cerca posto vicino al Teatro
        </button>
        <a data-testid="next-show-link" href={s.ticket_url || data.site_url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-amber-400 hover:text-amber-300 transition-colors">
          Programma completo <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>
    </div>
  );
};
